import type { NavigateFunction } from "react-router-dom";
import API_MIDDLEWARE from "../redux/api/middleware";
import {
  NOTIFICATIONS_LIST_URL,
  NOTIFICATIONS_READ_ALL_URL,
  NOTIFICATIONS_READ_URL,
  NOTIFICATIONS_UNREAD_COUNT_URL,
} from "../redux/api/config";
import { ROUTE_COMPLETE_PROFILE, ROUTE_JOB_DETAILS } from "../constants/routes";

export type ApiNotification = {
  id: number;
  userId: number;
  title: string;
  body: string;
  type: string;
  data?: Record<string, string>;
  read: boolean;
  readAt: string | null;
  createdAt: string;
  devicesNotified?: number | null;
  devicesFailed?: number | null;
};

export function getNotificationJobId(data?: Record<string, string>): string | null {
  if (!data) return null;
  const raw = data.jobId ?? data.jobid ?? data.job_id;
  if (raw === undefined || raw === null || raw === "") return null;
  const id = String(raw).trim();
  return id || null;
}

export type HighlightSection = "recommended" | "applied";

export type NotificationNavigationTarget =
  | { type: "job-details"; jobId: string }
  | {
      type: "my-activities";
      activitiesTab: 0 | 1;
      section: HighlightSection;
      jobId?: string;
    }
  | { type: "route"; path: string };

const MY_ACTIVITIES_TYPES = new Set([
  "JOB_RECOMMENDED",
  "APPLICATION_SHORTLISTED",
  "APPLICATION_DECLINED",
]);

function isMyActivitiesNotificationType(type: string): boolean {
  const t = type.toUpperCase();
  if (MY_ACTIVITIES_TYPES.has(t)) return true;
  if (t.includes("RECOMMEND")) return true;
  if (t.includes("SHORTLIST")) return true;
  if (t.includes("DECLIN") || t.includes("REJECT")) return true;
  return false;
}

export function getMyActivitiesHighlightSection(type: string): HighlightSection {
  const t = type.toUpperCase();
  if (t === "JOB_RECOMMENDED" || t.includes("RECOMMEND")) return "recommended";
  return "applied";
}

function isJobCreatedNotificationType(type: string): boolean {
  const t = type.toUpperCase();
  if (t === "JOB_CREATED" || t === "NEW_JOB" || t === "JOB_POSTED") return true;
  if (t.includes("JOB") && (t.includes("CREAT") || t.includes("POSTED") || t.includes("NEW_JOB"))) {
    return !t.includes("APPLICATION") && !t.includes("RECOMMEND");
  }
  return false;
}

/** Resolve where an in-app notification list item should navigate. */
export function getNotificationNavigationTarget(
  item: ApiNotification
): NotificationNavigationTarget | null {
  return resolveNotificationNavigation(item.type ?? "", item.data);
}

/** Resolve navigation from FCM / push payload data fields. */
export function resolveNotificationNavigation(
  type: string,
  data?: Record<string, string>
): NotificationNavigationTarget | null {
  if (isMyActivitiesNotificationType(type)) {
    const section = getMyActivitiesHighlightSection(type);
    const activitiesTab: 0 | 1 = section === "recommended" ? 0 : 1;
    const jobId = getNotificationJobId(data);
    return { type: "my-activities", activitiesTab, section, jobId: jobId ?? undefined };
  }

  const jobId = getNotificationJobId(data);
  if (jobId && isJobCreatedNotificationType(type)) {
    return { type: "job-details", jobId };
  }

  const route = data?.route;
  if (route?.startsWith("/")) {
    return { type: "route", path: route };
  }

  return null;
}

export function buildMyActivitiesHighlightUrl(
  jobId: string,
  section: HighlightSection
): string {
  const params = new URLSearchParams({
    highlightJobId: jobId,
    highlightSection: section,
  });
  return `${ROUTE_COMPLETE_PROFILE}?${params.toString()}`;
}

export function navigateToNotificationTarget(
  navigate: NavigateFunction,
  target: NotificationNavigationTarget
): void {
  if (target.type === "job-details") {
    navigate(`${ROUTE_JOB_DETAILS}/${target.jobId}`, { state: { fromBrowseJobs: true } });
    return;
  }
  if (target.type === "my-activities") {
    navigate(ROUTE_COMPLETE_PROFILE, {
      state: {
        activeTab: target.activitiesTab,
        highlightJobId: target.jobId,
        highlightSection: target.section,
      },
    });
    return;
  }
  if (target.type === "route") {
    navigate(target.path);
  }
}

export function hasNotificationNavigation(item: ApiNotification): boolean {
  return getNotificationNavigationTarget(item) !== null;
}

export type NotificationsListResult = {
  records: ApiNotification[];
  totalRecords: number;
  page: number;
  fetchSize: number;
  hasNext: boolean;
  unreadCount: number;
};

function parseUnreadCount(response: any): number {
  const payload = response?.result ?? response?.data ?? response;
  if (typeof payload === "number") return Math.max(0, payload);

  const value =
    payload?.unreadCount ??
    payload?.unread_count ??
    payload?.count ??
    payload?.unread ??
    payload?.totalUnread ??
    0;

  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

export const NOTIFICATIONS_UPDATED_EVENT = "seevi-notifications-updated";

export async function markAllNotificationsAsRead(): Promise<void> {
  await API_MIDDLEWARE(NOTIFICATIONS_READ_ALL_URL, "PATCH", {
    "Content-Type": "application/json",
  });
  window.dispatchEvent(new CustomEvent(NOTIFICATIONS_UPDATED_EVENT));
}

export async function markNotificationAsRead(notificationId: number): Promise<void> {
  await API_MIDDLEWARE(NOTIFICATIONS_READ_URL(notificationId), "PATCH", {
    "Content-Type": "application/json",
  });
  window.dispatchEvent(new CustomEvent(NOTIFICATIONS_UPDATED_EVENT));
}

export async function fetchUnreadNotificationCount(): Promise<number> {
  const response = await API_MIDDLEWARE(
    NOTIFICATIONS_UNREAD_COUNT_URL,
    "GET",
    { "Content-Type": "application/json" }
  );
  return parseUnreadCount(response);
}

export async function fetchNotificationsList(
  page = 1,
  fetchSize = 10,
  unreadOnly = false
): Promise<NotificationsListResult> {
  const params = new URLSearchParams({
    page: String(page),
    fetchSize: String(fetchSize),
    unreadOnly: String(unreadOnly),
  });

  const response = await API_MIDDLEWARE(
    `${NOTIFICATIONS_LIST_URL}?${params.toString()}`,
    "GET",
    { "Content-Type": "application/json" }
  );

  const result = response?.result ?? response ?? {};

  const list = result.notifications ?? result.records ?? [];
  const records = Array.isArray(list) ? list : [];

  const resolvedPage = Number(result.page) || page;
  const resolvedFetchSize = Number(result.fetchSize) || fetchSize;
  const totalRecords = Number(result.totalRecords) || records.length;
  const hasNext =
    result.hasNext !== undefined && result.hasNext !== null
      ? Boolean(result.hasNext)
      : records.length >= resolvedFetchSize;

  return {
    records,
    totalRecords,
    page: resolvedPage,
    fetchSize: resolvedFetchSize,
    hasNext,
    unreadCount: parseUnreadCount(response),
  };
}
